/**
 * MarkdownRenderer Component
 * 
 * Renders markdown content with support for:
 * - Standard markdown elements (headings, lists, blockquotes, etc.)
 * - LaTeX math expressions (via KaTeX)
 * - Custom table rendering with export functionality
 * - SQL syntax highlighting
 */

import React, { useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkMath from 'remark-math';
import remarkBreaks from 'remark-breaks';
import rehypeKatex from 'rehype-katex';
import 'katex/dist/katex.min.css';
import i18n from '@/shared/lib/i18n';
import logger from '@/shared/lib/logger';

import {
    splitContentByTables,
    hasMarkdownTables,
    escapeCurrencyForLatex,
    formatCellContent,
    isNumericHeader,
    stripRedundantChartHeadings,
} from '@/shared/lib/markdown';
import { MarkdownTable } from './MarkdownTable';
import { CodeBlock } from './components/CodeBlock';
import { RechartsRenderer } from '@/shared/components/charts';
import { ErrorBoundary } from '@/shared/components/ErrorBoundary';


// =============================================================================
// Types
// =============================================================================

interface MarkdownRendererProps {
    content: string;
    className?: string;
    enableLatex?: boolean;
}

interface MarkdownComponentProps {
    children?: React.ReactNode;
    className?: string;
    href?: string;
    node?: unknown;
}

function removeControlCharacters(value: string): string {
    return Array.from(value)
        .filter((char) => {
            const code = char.charCodeAt(0);
            const isAsciiControl = code < 32 || code === 127;
            const isAllowedWhitespace = code === 9 || code === 10 || code === 13;
            return !isAsciiControl || isAllowedWhitespace;
        })
        .join('');
}

function normalizeEscapedCurrency(value: string): string {
    // Alguns modelos retornam moeda escapada em texto normal (ex.: R\$12.000).
    // Para exibição Markdown, normalizamos de volta para R$12.000.
    return value.replace(/\b([A-Za-z]{1,5})\\\$(?=\s*\d)/g, '$1$');
}

/**
 * Links do tipo [1](url)[2](url) no Markdown viram texto "1" e "2" em dois <a> seguidos,
 * parecendo "12". Detectamos citações de grounding/web e mostramos [1], [2] com separação.
 */
function isNumericWebCitation(href: string | undefined, linkText: string): boolean {
    if (!href || !/^\d+$/.test(linkText.trim())) return false;
    const h = href.toLowerCase();
    return (
        h.includes('vertexaisearch.cloud.google.com') ||
        h.includes('grounding-api-redirect') ||
        (h.includes('google.com/search') && h.includes('q='))
    );
}

// =============================================================================
// Memoized Component Maps (defined outside render for performance)
// =============================================================================

/**
 * Creates the component map for ReactMarkdown.
 * Defined as a function to allow for consistent usage across renders.
 */
const createMarkdownComponents = () => ({
    // Headings
    h1: ({ children }: MarkdownComponentProps) => (
        <h1 className="text-2xl font-bold mb-4 text-slate-900 dark:text-slate-100">
            {children}
        </h1>
    ),
    h2: ({ children }: MarkdownComponentProps) => (
        <h2 className="text-xl font-semibold mb-3 text-slate-900 dark:text-slate-100">
            {children}
        </h2>
    ),
    h3: ({ children }: MarkdownComponentProps) => (
        <h3 className="text-lg font-medium mb-2 text-slate-900 dark:text-slate-100">
            {children}
        </h3>
    ),

    // Paragraphs
    p: ({ children }: MarkdownComponentProps) => (
        <p className="mb-2 leading-relaxed text-slate-700 dark:text-slate-300">
            {children}
        </p>
    ),

    // Lists
    ul: ({ children }: MarkdownComponentProps) => (
        <ul className="list-disc list-outside mb-2 space-y-1">
            {children}
        </ul>
    ),
    ol: ({ children }: MarkdownComponentProps) => (
        <ol className="list-decimal list-outside mb-2 space-y-1">
            {children}
        </ol>
    ),
    li: ({ children }: MarkdownComponentProps) => (
        <li className="text-slate-700 dark:text-slate-300">
            {children}
        </li>
    ),

    // Inline formatting
    strong: ({ children }: MarkdownComponentProps) => (
        <strong className="font-semibold text-slate-900 dark:text-slate-100">
            {children}
        </strong>
    ),
    em: ({ children }: MarkdownComponentProps) => (
        <em className="italic text-slate-600 dark:text-slate-400">
            {children}
        </em>
    ),

    // Code (uses CodeBlock component or RechartsRenderer)
    code: ({ children, className }: MarkdownComponentProps) => {
        const match = /language-(\w+)/.exec(className || '');
        const language = match ? match[1] : '';
        const content = String(children).replace(/\n$/, '');

        if (language === 'recharts') {
            try {
                // Limpar o JSON antes de parsear (remover caracteres problemáticos)
                const cleanedContent = removeControlCharacters(content)
                    .replace(/\uFEFF/g, '') // Remove BOM characters
                    .replace(/\u200B/g, '') // Remove zero-width spaces
                    .replace(/\\\$/g, '$') // Fix escaped dollar signs (LaTeX: R\$ → R$)
                    .replace(/,\s*}/g, '}') // Remove trailing commas
                    .replace(/,\s*]/g, ']') // Remove trailing commas in arrays
                    .trim();

                const config = JSON.parse(cleanedContent);

                // Blocos escritos manualmente pelo LLM sem recharts_tool:
                // 1. Formato legado: {config, data} sem yAxis
                // 2. Formato de input da tool: {chart_type, inputs, data} em snake_case
                // O backend remove esses blocos em novas mensagens; este guard protege
                // mensagens antigas salvas no histórico.
                const isLegacyManual = config && typeof config === 'object' && 'config' in config && 'data' in config && !('yAxis' in config);
                const isToolInputFormat = config && typeof config === 'object' && 'chart_type' in config && 'inputs' in config && !('chartType' in config);
                if (isLegacyManual || isToolInputFormat) {
                    return null;
                }

                return (
                    <div className="my-4">
                        <ErrorBoundary
                            fallback={
                                <div className="p-4 border border-yellow-200 bg-yellow-50 dark:bg-yellow-950/30 text-yellow-700 dark:text-yellow-400 rounded">
                                    <p className="text-sm">{i18n.t("shared:charts.renderError")}</p>
                                </div>
                            }
                        >
                            <RechartsRenderer config={config} title={config.title} />
                        </ErrorBoundary>
                    </div>
                );
            } catch (e) {
                logger.error("Failed to parse recharts JSON:", e);
                return (
                    <div className="p-4 border border-red-200 bg-red-50 dark:bg-red-950/30 text-red-600 dark:text-red-400 rounded space-y-2">
                        <p className="font-medium">{i18n.t("shared:markdown.chartRenderError")}</p>
                        <details className="text-xs">
                            <summary className="cursor-pointer hover:underline">{i18n.t("shared:markdown.viewErrorDetails")}</summary>
                            <pre className="mt-2 p-2 bg-red-100 dark:bg-red-900/30 rounded overflow-x-auto max-h-40">
                                {content.substring(0, 300)}...
                            </pre>
                        </details>
                    </div>
                );
            }
        }

        return <CodeBlock className={className}>{children}</CodeBlock>;
    },
    pre: ({ children }: MarkdownComponentProps) => (
        <div className="mb-4">{children}</div>
    ),

    // Blockquote
    blockquote: ({ children }: MarkdownComponentProps) => (
        <blockquote className="border-l-4 border-blue-500 pl-4 py-2 mb-4 bg-blue-50 dark:bg-blue-950/30 italic text-slate-700 dark:text-slate-300">
            {children}
        </blockquote>
    ),

    // Links (citações [n](url) adjacentes: sem colchetes visíveis o browser mostra "12" em vez de [1][2])
    a: ({ children, href }: MarkdownComponentProps) => {
        const childStr = String(children ?? '').trim();
        const isCitation = isNumericWebCitation(href, childStr);
        return (
            <a
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                title={isCitation ? i18n.t('shared:markdown.citationTooltip', { n: childStr }) : undefined}
                className={
                    isCitation
                        ? 'inline-block mx-0.5 whitespace-nowrap text-teal-700 dark:text-teal-400 hover:text-teal-900 dark:hover:text-teal-300 underline font-medium text-sm align-baseline'
                        : 'text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 underline'
                }
            >
                {isCitation ? `[${childStr}]` : children}
            </a>
        );
    },

    // Tables (fallback for ReactMarkdown's built-in table handling)
    table: ({ children }: MarkdownComponentProps) => (
        <div className="overflow-x-auto mb-4 border border-slate-200 dark:border-border/50 rounded-lg shadow-sm">
            <table className="min-w-full divide-y divide-slate-200 dark:divide-border/30">
                {children}
            </table>
        </div>
    ),
    thead: ({ children }: MarkdownComponentProps) => (
        <thead className="bg-slate-50 dark:bg-muted/50">
            {children}
        </thead>
    ),
    tbody: ({ children }: MarkdownComponentProps) => (
        <tbody className="bg-white dark:bg-card divide-y divide-slate-200 dark:divide-border/30">
            {children}
        </tbody>
    ),
    tr: ({ children }: MarkdownComponentProps) => (
        <tr className="hover:bg-slate-50 dark:hover:bg-muted/40 transition-colors duration-150">
            {children}
        </tr>
    ),
    th: ({ children }: MarkdownComponentProps) => {
        const content = String(children || '');
        const isNumeric = isNumericHeader(content);
        return (
            <th className={`px-3 py-2 sm:px-6 sm:py-3 text-xs font-medium text-slate-600 dark:text-white/80 uppercase tracking-wider whitespace-nowrap border-b border-slate-200 dark:border-border/50 bg-slate-50 dark:bg-muted/50 ${isNumeric ? 'text-right' : 'text-left'}`}>
                {children}
            </th>
        );
    },
    td: ({ children }: MarkdownComponentProps) => {
        const content = String(children || '');
        const { type } = formatCellContent(content);
        const isNumericType = type !== 'text';

        let colorClass = 'text-slate-900 dark:text-foreground';
        if (type === 'currency') colorClass = 'text-accent';
        if (type === 'percentage') colorClass = 'text-blue-700 dark:text-blue-400';

        return (
            <td className={`px-3 py-2 sm:px-6 sm:py-4 text-sm whitespace-nowrap border-b border-slate-200 dark:border-border/30 ${isNumericType ? 'text-right font-mono font-medium' : 'text-left'} ${colorClass}`}>
                {children}
            </td>
        );
    },
});

// Create components once (memoized at module level)
const markdownComponents = createMarkdownComponents();

// =============================================================================
// Main Component
// =============================================================================

/**
 * Renders markdown content with enhanced styling and features.
 * 
 * Features:
 * - Automatic table detection and custom rendering
 * - LaTeX math support (only when needed)
 * - SQL syntax highlighting
 * - Proper handling of Brazilian currency symbols
 */
export function MarkdownRenderer({ content, className = '', enableLatex = true }: MarkdownRendererProps) {
    // Check if content has tables that need custom rendering
    const containsTables = useMemo(() => hasMarkdownTables(content), [content]);

    // If content has tables, split and render with MarkdownTable
    if (containsTables) {
        return <ContentWithTables content={content} className={className} enableLatex={enableLatex} />;
    }

    // Otherwise, render normally
    return <NormalContent content={content} className={className} enableLatex={enableLatex} />;
}

// =============================================================================
// Internal Components
// =============================================================================

interface ContentProps {
    content: string;
    className: string;
    enableLatex?: boolean;
}

/**
 * Renders content that contains markdown tables.
 * Splits content into parts and renders tables with MarkdownTable component.
 */
const ContentWithTables: React.FC<ContentProps> = ({ content, className, enableLatex = true }) => {
    const parts = useMemo(() => splitContentByTables(content), [content]);

    if (parts.length === 0) {
        return <NormalContent content={content} className={className} enableLatex={enableLatex} />;
    }

    return (
        <div className={`markdown-content ${className}`}>
            {parts.map((part, index) => {
                if (part.type === 'table') {
                    return <MarkdownTable key={index} content={part.content} />;
                }
                return (
                    <div key={index}>
                        <NormalContent content={part.content} className="" enableLatex={enableLatex} />
                    </div>
                );
            })}
        </div>
    );
};

/**
 * Renders normal markdown content (without custom table handling).
 * Automatically enables LaTeX support when math expressions are detected.
 */
const NormalContent: React.FC<ContentProps> = ({ content: rawContent, className, enableLatex = true }) => {
    const normalizedRawContent = useMemo(
        () => stripRedundantChartHeadings(normalizeEscapedCurrency(rawContent)),
        [rawContent]
    );

    // Check if raw content contains real LaTeX (and not currency like R$ 10.00)
    const hasLatex = useMemo(() => {
        if (!enableLatex) return false;

        // LaTeX commands/delimiters: \alpha, \frac, \( ... \), \[ ... \]
        const hasLatexCommandsOrDelimiters =
            /\\[a-zA-Z]+/.test(normalizedRawContent) || /\\\(|\\\[/.test(normalizedRawContent);

        // Inline math with $...$ using unescaped dollars, excluding currency ($10, R$10, US$10)
        const hasInlineMath =
            /(?<!\\)\$(?!\s?\d)([^$\n]|\\\$)+(?<!\\)\$/.test(normalizedRawContent);

        // $...$ pair whose body carries a math token (^ _ { } \) — real inline math
        // even when it starts with a digit, e.g. $10^{15}$. Currency ("$10", "de $10 a $20")
        // has no such token between the dollars, so it is not misdetected here.
        const hasInlineMathWithTokens =
            /(?<!\\)\$[^$\n]*[\^_{}\\][^$\n]*\$/.test(normalizedRawContent);

        // Display math block: $$...$$ (may span multiple lines).
        const hasDisplayMath = /(?<!\\)\$\$[\s\S]*?\$\$/.test(normalizedRawContent);

        return hasLatexCommandsOrDelimiters || hasInlineMath || hasInlineMathWithTokens || hasDisplayMath;
    }, [normalizedRawContent, enableLatex]);

    // Só aplica escape de "$" quando vamos realmente renderizar com plugins LaTeX.
    // Caso contrário, manter o texto cru evita mostrar "R\$" ao usuário.
    const content = useMemo(
        () => (hasLatex ? escapeCurrencyForLatex(normalizedRawContent) : normalizedRawContent),
        [normalizedRawContent, hasLatex]
    );

    // Render without LaTeX plugins for performance when not needed
    if (!hasLatex) {
        return (
            <div className={`markdown-content ${className}`}>
                <ReactMarkdown
                    remarkPlugins={[remarkBreaks]}
                    components={markdownComponents}
                    skipHtml={false}
                >
                    {content}
                </ReactMarkdown>
            </div>
        );
    }

    // Render with LaTeX support
    return (
        <div className={`markdown-content ${className}`}>
            <ReactMarkdown
                remarkPlugins={[remarkMath, remarkBreaks]}
                rehypePlugins={[
                    [rehypeKatex, {
                        throwOnError: false,
                        errorColor: '#cc0000',
                        strict: false,
                    }]
                ]}
                components={markdownComponents}
                skipHtml={false}
            >
                {content}
            </ReactMarkdown>
        </div>
    );
};
